import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { useProfile } from 'context/profile';
import type { ProductAccess as ProductAccessValues } from 'models/flexible/Client';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminShowClient, adminUpdateClient } from 'services/api/admin/clients';
import { adminGroupsQueryKeys, adminShowGroup, useInfiniteAdminGroups } from 'services/api/admin/groups';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

type ProductKey = keyof ProductAccessValues;
type Toggle = { enabled: boolean };
type ProductValue = Toggle & { [setting: string]: unknown };
type Values = Record<ProductKey, ProductValue> & {
  inboundReferrals: ProductValue & { centralIntakeGroupId?: string | null };
};

// A product's `features` are settings nested in its value (`{ enabled, <feature>: { enabled } }`) that only
// apply while the product is on.
export const PRODUCTS: { key: ProductKey; label: string; features?: { key: string; label: string }[] }[] = [
  { key: 'traditionalOlio', label: 'Care Coordination (Traditional Olio)' },
  { key: 'olioSummarization', label: 'Summarization Workspace' },
  { key: 'referralSummarization', label: 'Referral Summarization' },
  { key: 'dischargeCalendar', label: 'Discharge Calendar' },
  // Two separate products, two separate keys — the legacy Admissions review and the newer
  // Inbound Referrals. Disambiguated in the label only; the key must not change (OLIO-2905).
  { key: 'admissions', label: 'Admissions (legacy)' },
  { key: 'inboundReferrals', label: 'Inbound Referrals', features: [{ key: 'tourTracking', label: 'Tour Tracking' }] },
  { key: 'preferredProviders', label: 'Preferred Providers' },
];

function productValue(saved: Partial<Record<string, any>> | undefined, features: { key: string }[] = []): ProductValue {
  return {
    enabled: saved?.enabled ?? false,
    ...Object.fromEntries(features.map(({ key }) => [key, { enabled: saved?.[key]?.enabled ?? false }])),
  };
}

type GroupOption = { id: string; name: string };

export default function ProductAccess() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const { data: client, isLoading } = useQuery({
    queryKey: ['adminClient', id, 'productAccess'],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });

  const [values, setValues] = useState<Values | null>(null);
  const [serverValues, setServerValues] = useState<Values | null>(null);

  const centralIntakeGroupOptions = useInfiniteAdminGroups({ clientId: id, isProvider: true, sortBy: 'name asc' });
  const storedCentralIntakeGroupId = serverValues?.inboundReferrals.centralIntakeGroupId;
  const { data: storedCentralIntakeGroup } = useQuery({
    queryKey: adminGroupsQueryKeys.show(storedCentralIntakeGroupId!),
    queryFn: ({ signal }) => adminShowGroup(storedCentralIntakeGroupId!, signal),
    enabled: !!storedCentralIntakeGroupId,
  });
  const [pickedCentralIntakeGroup, setPickedCentralIntakeGroup] = useState<GroupOption | null>(null);
  const centralIntakeGroupId = values?.inboundReferrals.centralIntakeGroupId;
  const centralIntakeGroup =
    [pickedCentralIntakeGroup, storedCentralIntakeGroup].find((group) => group && group.id === centralIntakeGroupId) ??
    null;

  useEffect(() => {
    if (client?.productAccess) {
      const loaded = Object.fromEntries(
        PRODUCTS.map(({ key, features }) => [key, productValue(client.productAccess[key], features)])
      ) as Values;
      loaded.inboundReferrals = {
        ...loaded.inboundReferrals,
        centralIntakeGroupId: client.productAccess.inboundReferrals?.centralIntakeGroupId ?? null,
      };
      setValues(loaded);
      setServerValues(loaded);
    }
  }, [client]);

  const isDirty = JSON.stringify(values) !== JSON.stringify(serverValues);

  const mutation = useMutation({
    mutationFn: (payload: Partial<ProductAccessValues>) => adminUpdateClient(id!, payload),
    onSuccess: () => {
      addToast({ text: 'Product access successfully updated' });
      navigate('/admin/clients');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || 'An error occurred while updating product access';
      addToast({ text: msg });
    },
  });

  // Turning a product off turns its features off too, but keeps its other settings, so unticking Inbound Referrals
  // keeps its central intake group.
  const handleToggle = (key: ProductKey, features?: { key: string }[]) => {
    if (!values) return;
    const value = values[key];
    setValues({
      ...values,
      [key]: value.enabled ? { ...value, ...productValue(undefined, features) } : { ...value, enabled: true },
    });
  };

  const handleFeatureToggle = (key: ProductKey, featureKey: string) => {
    if (!values) return;
    const value = values[key];
    const feature = value[featureKey] as Toggle;
    setValues({ ...values, [key]: { ...value, [featureKey]: { enabled: !feature.enabled } } });
  };

  const handleCentralIntakeGroupChange = (group: GroupOption | null) => {
    if (!values) return;
    setPickedCentralIntakeGroup(group);
    setValues({ ...values, inboundReferrals: { ...values.inboundReferrals, centralIntakeGroupId: group?.id ?? null } });
  };

  const handleSave = () => {
    if (!values) return;
    mutation.mutate(values as unknown as ProductAccessValues);
  };

  const goBack = () => navigate('/admin/clients');

  if (isLoading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title='Product Access' subtitle={client?.name} />
      <StyledFormSection>
        {PRODUCTS.map(({ key, label, features }) => (
          <React.Fragment key={key}>
            <StyledCheckbox
              checked={values?.[key]?.enabled ?? false}
              onChange={() => handleToggle(key, features)}
              label={label}
              labelSize='14px'
              checkedColor={colors.blue100}
              disabled={disabled}
              size={14}
            />
            {features?.map((feature) => (
              <NestedCheckbox
                key={feature.key}
                checked={(values?.[key]?.[feature.key] as Toggle | undefined)?.enabled ?? false}
                onChange={() => handleFeatureToggle(key, feature.key)}
                label={feature.label}
                labelSize='14px'
                checkedColor={colors.blue100}
                disabled={disabled || !values?.[key]?.enabled}
                size={14}
              />
            ))}
            {key === 'inboundReferrals' && values?.inboundReferrals.enabled && (
              <CentralIntake data-testid='central-intake-group'>
                <InputGroup title='Central Intake Group (optional)'>
                  <Select<GroupOption>
                    inputId='central-intake-group-select'
                    placeholder='Select a group'
                    options={centralIntakeGroupOptions.options}
                    onDebouncedSearch={centralIntakeGroupOptions.onDebouncedSearch}
                    value={centralIntakeGroup}
                    onChange={handleCentralIntakeGroupChange}
                    getOptionValue={(group) => group.id}
                    getOptionLabel={(group) => group.name}
                    isClearable
                    disabled={disabled}
                  />
                </InputGroup>
                <HelpText>
                  Referrals from other clients go to this group instead of this client&apos;s buildings.
                </HelpText>
              </CentralIntake>
            )}
          </React.Fragment>
        ))}
      </StyledFormSection>
      <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant='ghost' onClick={goBack}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={disabled || !isDirty || mutation.isPending} loading={mutation.isPending}>
          Update Product Access
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;

const StyledFormSection = styled(FormSection)`
  display: flex;
  flex-direction: column;
`;

const CentralIntake = styled.div`
  margin: 0 0 16px 24px;
  max-width: 400px;
`;

const HelpText = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${colors.black50};
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 16px;

  &:first-child {
    margin-top: 8px;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const NestedCheckbox = styled(StyledCheckbox)`
  margin-left: 24px;
`;
